import { BIZ_CODE } from '@/common/constants';
import { request } from '@/utils/request';

const urls = {
  getTempToken: '/zcy/obs/token/getTempToken',
  getDownLoadUrl: '/zcy/obs/file/getDownLoadUrl',
  getUserInfo: '/api/synlogin/suspendedceiling/user',
  getAliasConfig: '/api/splinter/config/nameConfig/anonymous/getConfigs',
  getPurchaserSteps: '/api/sparta/module/purchaser',
  getTemporarySteps: '/api/sparta/module/temporary',
  checkSensitiveWords: '/api/sparta/common/checkSensitiveWords',
  checkSensitiveWordsBatch: '/api/sparta/common/bacthCheckSensitiveWords',
};

// 默认上传地址
const URL_UPLOAD = '/zcy/obs/file/upload';

/**
 * 获取oss上传凭证
 */
export const getOssSignature = params => {
  return request(urls.getTempToken, {
    params,
  });
};

/**
 * 去除特殊字符
 * 文件名存在特殊字符会导致文件下载失败
 */
const trim = str => {
  if (!str) {
    return '';
  }
  if (/^([a-zA-Z]:)?(\\[a-zA-Z0-9_-]+)+\\?/.test(str)) {
    const tmp = str?.split('\\');
    str = tmp[tmp.length - 1];
  }
  return str.replace(/ /g, '').replace(/,/g, '').replace(/\$/g, '');
};

/**
 * 裁剪文件名称的最大长度
 * 过滤特殊字符
 * 如果超出，则自动裁剪
 */
export const cutFileName = (name, maxLength) => {
  let filename = trim(name);
  if (filename.length > maxLength) {
    const splitArray = filename?.split('.');
    const len = splitArray.length;
    const suffix = splitArray[len - 1]; // 后缀
    const nameLength = maxLength - (suffix.length + 1);
    filename = `${name.substring(nameLength)}.${suffix}`;
  }
  return filename;
};

/**
 * 获取兼容版本，直接上传后台服务
 * @param options 参数
 */
export const getParamsCompatible = options => {
  const { bizCode, attachment = true, fileName, maxLength = 100 } = options;
  return {
    uploadHref: URL_UPLOAD,
    name: 'files',
    signature: {
      bizCode,
      attachment,
      fileName: cutFileName(fileName, maxLength),
    },
  };
};

/**
 * 获取实际可访问链接
 * @param {Object} params
 *  {
 *    fileId: file.fileId,
 *    bizCode,
 *  }
 */
export const getUrl = params => {
  return request(urls.getDownLoadUrl, {
    params,
  });
};

/**
 * 获取上传文件凭证信息
 */
const getSignatureInfo = async fileName => {
  const filename = encodeURI(cutFileName(fileName, 100));

  const disposition = `attachment;filename=${filename}`;
  const options = {
    fileNum: 1,
    bizCode: BIZ_CODE,
    fileName: filename,
    attachment: true,
  };
  return getOssSignature(options)
    .then(res => {
      if (!res || !res.success) {
        Promise.reject(res.error || '获取上传口令失败');
        return;
      }
      const token = res?.result || {};
      // 非 OSS 场景，直接上传
      if (token.mode !== 'oss') {
        return getParamsCompatible({ ...options, fileName });
      }
      const fileIdByToken = token?.data[0];
      const response = {
        signature: {
          OSSAccessKeyId: token?.accessId,
          policy: token?.policy,
          dir: token?.dir,
          signature: token?.signature,
          key: `${fileIdByToken}`,
          'Content-Disposition': disposition,
          // eslint-disable-next-line camelcase
          success_action_status: 200,
        },
        uploadHref: token?.host,
        name: 'file',
      };
      return response;
    })
    .catch(err => {});
};

/**
 * 上传文件
 * @param {File} file
 * @returns
 */
export const uploadFile = async file => {
  // 获取上传凭证
  const { signature, uploadHref, name } = await getSignatureInfo(file.name);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadHref);
    const data = new FormData();
    for (const item in signature) {
      if (signature[item]) {
        data.append(item, signature[item]);
      }
    }
    data.append(name, file);
    xhr.send(data);
    if (signature?.key) {
      xhr.addEventListener('load', () => {
        getUrl({
          bizCode: BIZ_CODE,
          fileId: signature?.key,
        }).then((res: any) => {
          const { result = '' } = res;
          resolve({
            url: result,
            fileId: signature?.key,
            fileType: file.type,
            bizCode: BIZ_CODE,
          });
        });
      });
    } else {
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          try {
            const { result } = JSON.parse(xhr.response);
            resolve({
              url: result?.downloadUrl,
              fileId: result?.fileId || signature?.key,
              fileType: file?.type,
              bizCode: BIZ_CODE,
            });
          } catch (error) {
            reject(error);
          }
        }
      };
    }
  });
};

/**
 * 根据文件url获取文件内容
 */
export const getFromFileUrl = url => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('get', url, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status === 200) {
        const reader = new FileReader();
        reader.readAsText(xhr.response);
        reader.onload = () => {
          resolve(reader.result);
        };
      } else {
        reject(xhr);
      }
    };
    xhr.send();
  });
};

export const downloadFileByUrl = (url, fileName) => {
  const x = new XMLHttpRequest();
  x.open('GET', url, true);
  x.responseType = 'blob';
  x.onload = () => {
    const url = window.URL.createObjectURL(x.response);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  };
  x.send();
};
